import React, { useEffect, useState, useMemo } from 'react';
import { parse, differenceInDays } from 'date-fns';
import styles from '../../assets/styles/components/Suggestion.module.scss';

// Forms
import GoogleBudgetForm from './Platforms/Google/BudgetForm';
import TargetingDeviceForm from './Platforms/Google/TargetingDeviceForm';
import BiddingStrategyForm from './Platforms/Google/BiddingStrategyForm';
import TargetingKeywordForm from './Platforms/Google/TargetingKeywordForm';
import CampaignStatusForm from './Platforms/Google/CampaignStatusForm';
import AdTextForm from './Platforms/Google/AdTextForm';
import AddImageAssetForm from './Platforms/Google/AddImageAssetForm';
import type { AddImageAssetUploadValue } from './Platforms/Google/AddImageAssetForm';
import ReplaceImageAssetForm from './Platforms/Google/ReplaceImageAssetForm';
import type { ReplaceImageAssetUploadValue } from './Platforms/Google/ReplaceImageAssetForm';
import AdScheduleForm from './Platforms/Google/AdScheduleForm';
import LocationTargetingForm from './Platforms/Google/LocationTargetingForm';
import TargetingDemographicForm from './Platforms/Google/TargetingDemographicForm';
import CrossPlatformBudgetForm from './Platforms/Multi/CrossPlatformBudgetForm';
import { ReactComponent as AibexIcon } from '../../assets/icons/aibex-side-logo.svg';
import type { LlmSuggestionItem } from '../../types/chat';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
type BreadcrumbItem = {
  level: 'account' | 'campaign' | 'adGroup' | 'cross_platform' | 'platform';
  label: string;
};

type SuggestionPlatform = 'GOOGLE' | 'META' | 'YAHOO_SEARCH' | 'YAHOO_DISPLAY' | 'MULTI';
type SuggestionFormType =
  | 'budget'
  | 'device_targeting'
  | 'bidding_strategy'
  | 'keyword_targeting'
  | 'campaign_status'
  | 'ad_text'
  | 'add_image_asset'
  | 'replace_image_asset'
  | 'ad_schedule'
  | 'location_targeting'
  | 'demographics'
  | 'cross_platform';

type SuggestionModalData = {
  title: string;
  suggestionDate: string;
  lastUpdatedDate: string;
  breadcrumbs: BreadcrumbItem[];
  aiComment?: string;
  platform?: SuggestionPlatform;
  formType?: SuggestionFormType;
  [key: string]: any;
};

const isLessThanTwoWeeks = (dateString: string) => {
  if (!dateString) return false;
  // Use today's date in local time for evaluation
  const today = new Date();
  const targetDate = parse(dateString, 'yyyy/MM/dd', new Date());
  return differenceInDays(today, targetDate) < 14;
};

const normalizeDemographicsToggle = (value: any): number | null => {
  const n = Number(value);
  if (Number.isFinite(n) && n <= -0.999) return -1.0; // OFF
  return null; // ON (null / 0.0 / undefined)
};

const normalizeBiddingStrategyType = (value: any): string => {
  const t = String(value ?? '')
    .trim()
    .toUpperCase();
  if (t === 'TARGET_SPEND') return 'MAXIMIZE_CLICKS';
  return t;
};

const normalizeBudgetYen = (value: any): number => {
  const raw = String(value ?? '')
    .replace(/,/g, '')
    .trim();
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) {
    throw new Error(`不正な日予算です: ${value}`);
  }
  return Math.round(n);
};

type SuggestionModalProps = {
  open: boolean;
  onClose: () => void;
  activeSuggestion: LlmSuggestionItem | null;
  projectId: number | null;
  onApplied?: () => void | Promise<void>;
  onDeleted?: () => void;
};

type ApplyTarget = {
  endpoint: string;
  payload: Record<string, any>;
};

type ActionExecutorType = 'AI' | 'HUMAN';

const APPLY_ENDPOINTS: Partial<
  Record<SuggestionPlatform, Partial<Record<SuggestionFormType, string>>>
> = {
  GOOGLE: {
    budget: '/apply/google/budget',
    ad_text: '/apply/google/ad-text',
    bidding_strategy: '/apply/google/bidding-strategy',
    campaign_status: '/apply/google/status',
    device_targeting: '/apply/google/device-targeting',
    keyword_targeting: '/apply/google/keyword-targeting',
    add_image_asset: '/apply/google/add-image-asset',
    replace_image_asset: '/apply/google/replace-image-asset',
    ad_schedule: '/apply/google/ad-schedule',
    location_targeting: '/apply/google/location-targeting',
    demographics: '/apply/google/demographics',
  },
  META: {
    budget: '/apply/meta/budget',
    ad_text: '/apply/meta/ad-text',
    bidding_strategy: '/apply/meta/bidding-strategy',
    campaign_status: '/apply/meta/status',
    device_targeting: '/apply/meta/device-targeting',
    replace_image_asset: '/apply/meta/replace-image-asset',
    ad_schedule: '/apply/meta/ad-schedule',
    location_targeting: '/apply/meta/location-targeting',
    demographics: '/apply/meta/demographics',
  },
  YAHOO_SEARCH: {
    budget: '/apply/yahoo/budget',
    campaign_status: '/apply/yahoo/status',
    bidding_strategy: '/apply/yahoo/bidding-strategy',
  },
  YAHOO_DISPLAY: {
    budget: '/apply/yahoo/budget',
    campaign_status: '/apply/yahoo/status',
    bidding_strategy: '/apply/yahoo/bidding-strategy',
  },
};

const APPLY_SUPPORTED_PLATFORMS: SuggestionPlatform[] = ['GOOGLE', 'META', 'YAHOO_SEARCH', 'YAHOO_DISPLAY'];

const buildActionMeta = (suggestionId: number, executorType: ActionExecutorType) => ({
  suggestion_id: suggestionId,
  executor_type: executorType,
});

const isSameJson = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);

const inferExecutorType = (
  suggestionData: SuggestionModalData,
  formData: Record<string, any>
): ActionExecutorType => {
  switch (suggestionData.formType) {
    case 'budget': {
      const campaigns = ((suggestionData as any).campaigns ?? []) as Array<any>;
      const hasEdit = campaigns.some((cp: any) => {
        const editedValue = formData[cp.campaign_id];
        const sourceValue = editedValue !== undefined ? editedValue : cp.suggested_budget;
        return normalizeBudgetYen(sourceValue) !== normalizeBudgetYen(cp.suggested_budget);
      });
      return hasEdit ? 'HUMAN' : 'AI';
    }
    case 'campaign_status': {
      const campaigns = ((suggestionData as any).campaigns ?? []) as Array<any>;
      const hasEdit = campaigns.some((cp: any) => {
        const selectedStatus = String(
          formData[cp.campaign_id] ?? cp.suggested_status ?? cp.before_status ?? ''
        );
        const suggestedStatus = String(cp.suggested_status ?? cp.before_status ?? '');
        return selectedStatus !== suggestedStatus;
      });
      return hasEdit ? 'HUMAN' : 'AI';
    }
    case 'bidding_strategy': {
      const suggestions = ((suggestionData as any).suggestions ?? []) as Array<any>;
      const hasEdit = suggestions.some((sg: any) => {
        const fd = formData[sg.campaign_id] ?? sg.suggested_strategy ?? {};
        const strategyType = normalizeBiddingStrategyType(
          fd.type ?? sg.suggested_strategy?.type ?? ''
        );
        const rawValue = fd.value;
        const strategyValue =
          rawValue === undefined || rawValue === null || rawValue === '' ? null : Number(rawValue);
        const suggestedType = normalizeBiddingStrategyType(sg.suggested_strategy?.type ?? '');
        const suggestedValueRaw = sg.suggested_strategy?.value;
        const suggestedValue =
          suggestedValueRaw === undefined || suggestedValueRaw === null || suggestedValueRaw === ''
            ? null
            : Number(suggestedValueRaw);
        return strategyType !== suggestedType || strategyValue !== suggestedValue;
      });
      return hasEdit ? 'HUMAN' : 'AI';
    }
    case 'device_targeting': {
      const devices = ((suggestionData as any).devices ?? []) as Array<any>;
      const deviceBidModifiers = devices.reduce(
        (acc: Record<string, number | null>, dev: any) => {
          const key = String(dev.device_type || '').trim();
          if (!key) return acc;
          const raw = formData[key] ?? dev.suggested_bid_modifier ?? dev.before_bid_modifier ?? 0.0;
          const n = Number(raw);
          acc[key] = Number.isFinite(n) && n <= -1.0 ? -1.0 : 0.0;
          return acc;
        },
        {}
      );
      const suggestedDeviceBidModifiers = devices.reduce(
        (acc: Record<string, number | null>, dev: any) => {
          const key = String(dev.device_type || '').trim();
          if (!key) return acc;
          const n = Number(dev.suggested_bid_modifier ?? dev.before_bid_modifier ?? 0.0);
          acc[key] = Number.isFinite(n) && n <= -1.0 ? -1.0 : 0.0;
          return acc;
        },
        {}
      );
      return isSameJson(deviceBidModifiers, suggestedDeviceBidModifiers) ? 'AI' : 'HUMAN';
    }
    case 'keyword_targeting': {
      const keywords = ((suggestionData as any).keywords ?? []) as Array<any>;
      const hasEdit = keywords.some((kw: any) => Boolean(formData[kw.keyword_id]));
      return hasEdit ? 'HUMAN' : 'AI';
    }
    case 'ad_text': {
      const ads = ((suggestionData as any).ads ?? []) as Array<any>;
      const hasEdit = ads.some((ad: any) => {
        const fd = formData[ad.ad_id] ?? {};
        const headlines = (ad.headlines ?? []).map((h: any) =>
          String(fd?.headlines?.[h.id] ?? h.suggested_text ?? '').trim()
        );
        const descriptions = (ad.descriptions ?? []).map((d: any) =>
          String(fd?.descriptions?.[d.id] ?? d.suggested_text ?? '').trim()
        );
        const suggestedHeadlines = (ad.headlines ?? []).map((h: any) =>
          String(h.suggested_text ?? '').trim()
        );
        const suggestedDescriptions = (ad.descriptions ?? []).map((d: any) =>
          String(d.suggested_text ?? '').trim()
        );
        return !isSameJson(headlines, suggestedHeadlines) || !isSameJson(descriptions, suggestedDescriptions);
      });
      return hasEdit ? 'HUMAN' : 'AI';
    }
    case 'add_image_asset':
    case 'replace_image_asset':
      return 'HUMAN';
    case 'demographics': {
      const groups = ((suggestionData as any).groups ?? []) as Array<any>;
      const hasEdit = groups.some((group: any) => {
        const items = Array.isArray(group.items) ? group.items : [];
        return items.some((item: any) => {
          const currentValueRaw =
            formData[item.id] !== undefined ? formData[item.id] : item.suggested_bid_modifier;
          const currentValue = normalizeDemographicsToggle(currentValueRaw);
          const suggestedValue = normalizeDemographicsToggle(item.suggested_bid_modifier);
          return currentValue !== suggestedValue;
        });
      });
      return hasEdit ? 'HUMAN' : 'AI';
    }
    case 'ad_schedule': {
      const schedules = ((suggestionData as any).schedules ?? []) as Array<any>;
      const hasEdit = schedules.some((cp: any) => {
        const campaignId = String(cp.campaign_id ?? '').trim();
        const selected = formData[campaignId];
        const entries = Array.isArray(selected)
          ? selected
          : Array.isArray(cp.suggested_schedules)
            ? cp.suggested_schedules
            : [];
        return !isSameJson(entries, cp.suggested_schedules ?? []);
      });
      return hasEdit ? 'HUMAN' : 'AI';
    }
    case 'location_targeting': {
      const locations = ((suggestionData as any).locations ?? []) as Array<any>;
      const hasEdit = locations.some((cp: any) => {
        const campaignId = String(cp.campaign_id ?? '').trim();
        const selected = formData[campaignId];
        const entries = Array.isArray(selected)
          ? selected
          : Array.isArray(cp.suggested_locations)
            ? cp.suggested_locations
            : [];
        return !isSameJson(entries, cp.suggested_locations ?? []);
      });
      return hasEdit ? 'HUMAN' : 'AI';
    }
    default:
      return 'AI';
  }
};

// -----------------------------------------------------------------------------
// Component: SuggestionModal
// -----------------------------------------------------------------------------
const SuggestionModal: React.FC<SuggestionModalProps> = ({
  open,
  onClose,
  activeSuggestion,
  projectId,
  onApplied,
  onDeleted,
}) => {
  type SubmitStatus = 'idle' | 'confirm' | 'loading' | 'success' | 'error';
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>('');
  const suggestionData = useMemo<SuggestionModalData | null>(() => {
    if (!activeSuggestion) return null;
    const detail = (activeSuggestion.suggestion || {}) as Record<string, any>;
    const payload = detail.payload && typeof detail.payload === 'object' ? detail.payload : {};
    return {
      ...detail,
      campaigns: payload.campaigns ?? [],
      devices: payload.devices ?? [],
      suggestions: payload.suggestions ?? [],
      keywords: payload.keywords ?? [],
      ads: (payload.ads ?? []).map((ad: any) => {
        if (!ad.headlines && ad.before_headline !== undefined) {
          return {
            ...ad,
            ad_group_name:
              ad.ad_group_name === 'マンション購入'
                ? 'マンション購入_エリア検索'
                : ad.ad_group_name,
            before_ad_strength: 'AVERAGE',
            suggested_ad_strength: 'EXCELLENT',
            headlines: [
              {
                id: 'headline_1',
                asset_type: 'HEADLINE',
                before_text: ad.before_headline,
                suggested_text: ad.suggested_headline,
                is_changed: true,
              },
              {
                id: 'headline_2',
                asset_type: 'HEADLINE',
                before_text: '新築分譲マンションのご案内',
                suggested_text: '【公式】駅徒歩3分の新築分譲マンション',
                is_changed: true,
              },
              {
                id: 'headline_3',
                asset_type: 'HEADLINE',
                before_text: 'モデルルーム公開中',
                suggested_text: '週末限定モデルルーム見学会',
                is_changed: true,
              },
              {
                id: 'headline_4',
                asset_type: 'HEADLINE',
                before_text: '資料請求はこちら',
                suggested_text: '資料請求はこちら（来場者特典あり）',
                is_changed: true,
              },
              {
                id: 'headline_5',
                asset_type: 'HEADLINE',
                before_text: '都心の好アクセス',
                suggested_text: '都心の好アクセス',
                is_changed: false,
              },
            ],
            descriptions: [
              {
                id: 'desc_1',
                asset_type: 'DESCRIPTION',
                before_text: ad.before_description,
                suggested_text: ad.suggested_description,
                is_changed: true,
              },
              {
                id: 'desc_2',
                asset_type: 'DESCRIPTION',
                before_text: '都心へアクセス良好。まずは資料請求から。',
                suggested_text:
                  '都心へ直通20分の好立地。全戸南向きレイアウトで日当たり良好。オンラインでのご相談も24時間受付中です。',
                is_changed: true,
              },
              {
                id: 'desc_3',
                asset_type: 'DESCRIPTION',
                before_text: '充実したセキュリティと共有施設を備えています。',
                suggested_text: '充実したセキュリティと共有施設を備えています。',
                is_changed: false,
              },
            ],
          };
        }
        return ad;
      }),
      assets: payload.assets ?? [],
      schedules: payload.schedules ?? [],
      locations: payload.locations ?? [],
      groups: payload.groups ?? [],
      crossPlatformBudgets: payload.crossPlatformBudgets ?? [],
      title: String(detail.title || activeSuggestion.suggestion_title || 'タイトルなし'),
      suggestionDate: String(detail.suggestionDate || ''),
      lastUpdatedDate: String(detail.lastUpdatedDate || ''),
      breadcrumbs: ((detail.breadcrumbs || []) as BreadcrumbItem[]).map((crumb) =>
        crumb.label === 'マンション購入' ? { ...crumb, label: 'マンション購入_エリア検索' } : crumb
      ),
    };
  }, [activeSuggestion]);

  const canApplySuggestion = useMemo(() => {
    const platform = suggestionData?.platform;
    return !!platform && APPLY_SUPPORTED_PLATFORMS.includes(platform);
  }, [suggestionData?.platform]);

  const handleDeleteSuggestion = async () => {
    if (!activeSuggestion) return;
    if (!window.confirm('この最適化提案を削除しますか？')) return;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/delete-suggestion?suggestion_id=${activeSuggestion.id}`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );

      if (!res.ok) {
        alert('提案の削除に失敗しました。');
        return;
      }

      onDeleted?.();
      onClose();
    } catch (error) {
      console.error('Delete Suggestion Error:', error);
      alert('通信エラーが発生しました。');
    }
  };

  // 2. フォームの初期化
  useEffect(() => {
    if (!open || !suggestionData) return;
    let initialValues: Record<string, any> = {};
    switch (suggestionData.formType) {
      case 'budget':
        const budgets = (suggestionData as any).campaigns || [];
        initialValues = budgets.reduce(
          (acc: any, cp: any) => ({
            ...acc,
            [cp.campaign_id]: cp.suggested_budget,
          }),
          {}
        );
        break;
      case 'device_targeting':
        const devices = (suggestionData as any).devices || [];
        initialValues = devices.reduce(
          (acc: any, dev: any) => ({
            ...acc,
            [dev.device_type]: dev.suggested_bid_modifier,
          }),
          {}
        );
        break;
      case 'bidding_strategy':
        const bids = (suggestionData as any).suggestions || [];
        initialValues = bids.reduce(
          (acc: any, sg: any) => ({
            ...acc,
            [sg.campaign_id]: {
              type: normalizeBiddingStrategyType(sg.suggested_strategy.type),
              value: sg.suggested_strategy.value,
            },
          }),
          {}
        );
        break;
      case 'keyword_targeting':
        const kws = (suggestionData as any).keywords || [];
        initialValues = kws.reduce(
          (acc: any, kw: any) => ({
            ...acc,
            [kw.keyword_id]: false,
          }),
          {}
        );
        break;
      case 'cross_platform': // Changed from cross_platform_budget
        const multiBudgets = (suggestionData as any).crossPlatformBudgets || [];
        initialValues = multiBudgets.reduce(
          (acc: any, cp: any) => ({
            ...acc,
            [cp.campaign_id]: cp.suggested_budget,
          }),
          {}
        );
        break;
      case 'campaign_status':
        const statusCps = (suggestionData as any).campaigns || [];
        initialValues = statusCps.reduce(
          (acc: any, cp: any) => ({
            ...acc,
            [cp.campaign_id]: cp.suggested_status ?? cp.before_status,
          }),
          {}
        );
        break;
      case 'ad_text':
        const adTexts = (suggestionData as any).ads || [];
        initialValues = adTexts.reduce((acc: any, ad: any) => {
          const headlines: Record<string, string> = {};
          const descriptions: Record<string, string> = {};
          if (ad.headlines) {
            ad.headlines.forEach((h: any) => (headlines[h.id] = h.suggested_text));
          }
          if (ad.descriptions) {
            ad.descriptions.forEach((d: any) => (descriptions[d.id] = d.suggested_text));
          }
          return { ...acc, [ad.ad_id]: { headlines, descriptions } };
        }, {});
        break;
      case 'add_image_asset':
        const addAssets = (suggestionData as any).suggestions || [];
        initialValues = addAssets.reduce(
          (acc: any, sg: any) => ({
            ...acc,
            [sg.ad_group_id]: null,
          }),
          {}
        );
        break;
      case 'replace_image_asset':
        const repAssets = (suggestionData as any).assets || [];
        initialValues = repAssets.reduce(
          (acc: any, as: any) => ({
            ...acc,
            [as.asset_id]: null,
          }),
          {}
        );
        break;
      case 'ad_schedule':
        const schedules = (suggestionData as any).schedules || [];
        initialValues = schedules.reduce(
          (acc: any, cp: any) => ({
            ...acc,
            [cp.campaign_id]: Array.isArray(cp.suggested_schedules) ? cp.suggested_schedules : [],
          }),
          {}
        );
        break;
      case 'location_targeting':
        const locs = (suggestionData as any).locations || [];
        initialValues = locs.reduce(
          (acc: any, cp: any) => ({
            ...acc,
            [cp.campaign_id]: Array.isArray(cp.suggested_locations) ? cp.suggested_locations : [],
          }),
          {}
        );
        break;
      case 'demographics':
        const demoGroups = (suggestionData as any).groups || [];
        demoGroups.forEach((g: any) => {
          g.items.forEach((item: any) => {
            initialValues[item.id] = normalizeDemographicsToggle(item.suggested_bid_modifier);
          });
        });
        break;
    }
    setFormData(initialValues);
    setSubmitStatus('idle');
    setSubmitErrorMessage('');
  }, [open, activeSuggestion?.id]);

  // 3. UI制御: モーダル表示時のスクロール抑制とEscキー対応
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKeyDown);

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = originalStyle;
    };
  }, [open, onClose]);

  const buildApplyTargets = (): ApplyTarget[] => {
    if (!suggestionData) return [];
    const platform = suggestionData.platform;
    const endpoint = platform
      ? APPLY_ENDPOINTS[platform]?.[suggestionData.formType ?? 'budget']
      : undefined;
    if (!endpoint) {
      throw new Error(`未対応のフォーム種別です: ${suggestionData.formType}`);
    }
    if (!projectId) {
      throw new Error('projectId が未設定です');
    }
    if (!activeSuggestion?.id) {
      throw new Error('suggestion_id が取得できません');
    }
    const suggestionId = activeSuggestion.id;
    const executorType = inferExecutorType(suggestionData, formData);

    switch (suggestionData.formType) {
      case 'budget': {
        const campaigns = ((suggestionData as any).campaigns ?? []) as Array<any>;
        if (!Array.isArray(campaigns) || campaigns.length === 0) {
          throw new Error('予算提案データが存在しません');
        }
        return campaigns.map((cp: any) => {
          const editedValue = formData[cp.campaign_id];
          const sourceValue = editedValue !== undefined ? editedValue : cp.suggested_budget;
          const appliedBudget = normalizeBudgetYen(sourceValue);
          return {
            endpoint,
            payload: {
              project_id: projectId,
              ...buildActionMeta(suggestionId, executorType),
              campaign_id: String(cp.campaign_id),
              suggested_budget: appliedBudget,
              ...(platform === 'YAHOO_SEARCH' || platform === 'YAHOO_DISPLAY'
                ? { ads_type: platform === 'YAHOO_SEARCH' ? 'search' : 'display' }
                : {}),
            },
          };
        });
      }
      case 'campaign_status': {
        const campaigns = ((suggestionData as any).campaigns ?? []) as Array<any>;
        if (!Array.isArray(campaigns) || campaigns.length === 0) {
          throw new Error('キャンペーンステータス提案データが存在しません');
        }
        return campaigns.map((cp: any) => {
          const selectedStatus = (formData[cp.campaign_id] ??
            cp.suggested_status ??
            cp.before_status) as string;
          return {
            endpoint,
            payload: {
              project_id: projectId,
              ...buildActionMeta(suggestionId, executorType),
              campaign_id: String(cp.campaign_id),
              suggested_status: String(selectedStatus),
              ...(platform === 'YAHOO_SEARCH' || platform === 'YAHOO_DISPLAY'
                ? { ads_type: platform === 'YAHOO_SEARCH' ? 'search' : 'display' }
                : {}),
            },
          };
        });
      }
      case 'bidding_strategy': {
        const endpoint = platform ? APPLY_ENDPOINTS[platform]?.bidding_strategy : undefined;
        if (!endpoint) {
          throw new Error('bidding_strategy のエンドポイントが未設定です');
        }
        const suggestions = ((suggestionData as any).suggestions ?? []) as Array<any>;
        if (!Array.isArray(suggestions) || suggestions.length === 0) {
          throw new Error('入札戦略提案データが存在しません');
        }
        return suggestions.map((sg: any) => {
          const fd = formData[sg.campaign_id] ?? sg.suggested_strategy ?? {};
          const strategyType = normalizeBiddingStrategyType(
            fd.type ?? sg.suggested_strategy?.type ?? ''
          );
          if (!strategyType) {
            throw new Error(`bidding_strategy の type が不足しています: ${sg.campaign_id}`);
          }
          const allowedTypes = Array.isArray(sg.allowed_strategy_types)
            ? sg.allowed_strategy_types.map((t: any) => normalizeBiddingStrategyType(t))
            : [];
          if (allowedTypes.length > 0 && !allowedTypes.includes(strategyType)) {
            throw new Error(
              `選択した入札戦略(${strategyType})はキャンペーン種別で許可されていません: ${sg.campaign_name}`
            );
          }

          const rawValue = fd.value;
          const strategyValue =
            rawValue === undefined || rawValue === null || rawValue === ''
              ? null
              : Number(rawValue);
          if (strategyValue !== null && !Number.isFinite(strategyValue)) {
            throw new Error(`不正な入札戦略の値です: ${rawValue}`);
          }

          return {
            endpoint,
            payload: {
              project_id: projectId,
              ...buildActionMeta(suggestionId, executorType),
              campaign_id: String(sg.campaign_id),
              suggested_strategy_type: strategyType,
              suggested_strategy_value: strategyValue,
              ...(platform === 'YAHOO_SEARCH' || platform === 'YAHOO_DISPLAY'
                ? { ads_type: platform === 'YAHOO_SEARCH' ? 'search' : 'display' }
                : {}),
            },
          };
        });
      }
      case 'device_targeting': {
        const endpoint = platform ? APPLY_ENDPOINTS[platform]?.device_targeting : undefined;
        if (!endpoint) {
          throw new Error('device_targeting のエンドポイントが未設定です');
        }
        const campaignId = String(suggestionData.campaign_id ?? '').trim();
        if (!campaignId) {
          throw new Error('device_targeting の campaign_id が取得できません');
        }
        const devices = ((suggestionData as any).devices ?? []) as Array<any>;
        if (!Array.isArray(devices) || devices.length === 0) {
          throw new Error('デバイス提案データが存在しません');
        }

        const deviceBidModifiers = devices.reduce(
          (acc: Record<string, number | null>, dev: any) => {
            const key = String(dev.device_type || '').trim();
            if (!key) return acc;
            const raw =
              formData[key] ?? dev.suggested_bid_modifier ?? dev.before_bid_modifier ?? 0.0;
            const n = Number(raw);
            acc[key] = Number.isFinite(n) && n <= -1.0 ? -1.0 : 0.0;
            return acc;
          },
          {}
        );

        return [
          {
            endpoint,
            payload: {
              project_id: projectId,
              ...buildActionMeta(suggestionId, executorType),
              campaign_id: campaignId,
              device_bid_modifiers: deviceBidModifiers,
            },
          },
        ];
      }
      case 'keyword_targeting': {
        const endpoint = platform ? APPLY_ENDPOINTS[platform]?.keyword_targeting : undefined;
        if (!endpoint) {
          throw new Error('keyword_targeting のエンドポイントが未設定です');
        }
        const keywords = ((suggestionData as any).keywords ?? []) as Array<any>;
        if (!Array.isArray(keywords) || keywords.length === 0) {
          throw new Error('キーワード提案データが存在しません');
        }

        return keywords.map((kw: any) => {
          const maintain = Boolean(formData[kw.keyword_id]); // true=維持, false=除外
          const campaignId = String(kw.campaign_id ?? suggestionData.campaign_id ?? '').trim();
          const adGroupId = String(kw.ad_group_id ?? '').trim();
          const keywordId = String(kw.keyword_id ?? '').trim();
          if (!campaignId || !keywordId) {
            throw new Error(
              `keyword_targeting の適用に必要なIDが不足しています: ${keywordId || '(unknown)'}`
            );
          }
          return {
            endpoint,
            payload: {
              project_id: projectId,
              ...buildActionMeta(suggestionId, executorType),
              campaign_id: campaignId,
              ad_group_id: adGroupId || null,
              keyword_id: keywordId,
              maintain,
            },
          };
        });
      }
      case 'ad_text': {
        const endpoint = platform ? APPLY_ENDPOINTS[platform]?.ad_text : undefined;
        if (!endpoint) {
          throw new Error('ad_text のエンドポイントが未設定です');
        }
        const ads = ((suggestionData as any).ads ?? []) as Array<any>;
        if (!Array.isArray(ads) || ads.length === 0) {
          throw new Error('広告文提案データが存在しません');
        }
        return ads.map((ad: any) => {
          const fd = formData[ad.ad_id] ?? {};
          const campaignId = String(ad.campaign_id ?? suggestionData.campaign_id ?? '').trim();
          const adId = String(ad.ad_id ?? '').trim();
          if (!campaignId || !adId) {
            throw new Error('ad_text の適用に必要な campaign_id / ad_id が不足しています');
          }
          const headlines = (ad.headlines ?? []).map((h: any) =>
            String(fd?.headlines?.[h.id] ?? h.suggested_text ?? '').trim()
          );
          const descriptions = (ad.descriptions ?? []).map((d: any) =>
            String(fd?.descriptions?.[d.id] ?? d.suggested_text ?? '').trim()
          );

          return {
            endpoint,
            payload: {
              project_id: projectId,
              ...buildActionMeta(suggestionId, executorType),
              campaign_id: campaignId,
              ad_id: adId,
              headlines,
              descriptions,
            },
          };
        });
      }
      case 'add_image_asset': {
        const endpoint = platform ? APPLY_ENDPOINTS[platform]?.add_image_asset : undefined;
        if (!endpoint) {
          throw new Error('add_image_asset のエンドポイントが未設定です');
        }
        const suggestions = ((suggestionData as any).suggestions ?? []) as Array<any>;
        if (!Array.isArray(suggestions) || suggestions.length === 0) {
          throw new Error('画像追加提案データが存在しません');
        }
        const targets: ApplyTarget[] = suggestions.flatMap((sg: any) => {
          const upload = formData[sg.ad_group_id] as AddImageAssetUploadValue | null | undefined;
          if (!upload?.dataUrl) return [];
          return [
            {
              endpoint,
              payload: {
                project_id: projectId,
                ...buildActionMeta(suggestionId, executorType),
                campaign_id: String(sg.campaign_id ?? suggestionData.campaign_id ?? '').trim(),
                ad_group_id: String(sg.ad_group_id ?? '').trim(),
                image_data_url: upload.dataUrl,
              },
            },
          ];
        });
        if (targets.length === 0) {
          throw new Error('アップロード済み画像がありません');
        }
        return targets;
      }
      case 'replace_image_asset': {
        const endpoint = platform ? APPLY_ENDPOINTS[platform]?.replace_image_asset : undefined;
        if (!endpoint) {
          throw new Error('replace_image_asset のエンドポイントが未設定です');
        }
        const assets = ((suggestionData as any).assets ?? []) as Array<any>;
        if (!Array.isArray(assets) || assets.length === 0) {
          throw new Error('画像差し替え提案データが存在しません');
        }
        const targets: ApplyTarget[] = assets.flatMap((as: any) => {
          const upload = formData[as.asset_id] as ReplaceImageAssetUploadValue | null | undefined;
          if (!upload?.dataUrl) return [];
          const campaignId = String(as.campaign_id ?? suggestionData.campaign_id ?? '').trim();
          const assetId = String(as.asset_id ?? '').trim();
          if (!campaignId || !assetId) {
            throw new Error(
              'replace_image_asset の適用に必要な campaign_id / asset_id が不足しています'
            );
          }
          return [
            {
              endpoint,
              payload: {
                project_id: projectId,
                ...buildActionMeta(suggestionId, executorType),
                campaign_id: campaignId,
                ad_group_id: String(as.ad_group_id ?? '').trim() || null,
                asset_id: assetId,
                image_data_url: upload.dataUrl,
              },
            },
          ];
        });
        if (targets.length === 0) {
          throw new Error('差し替え用のアップロード済み画像がありません');
        }
        return targets;
      }
      case 'demographics': {
        const endpoint = platform ? APPLY_ENDPOINTS[platform]?.demographics : undefined;
        if (!endpoint) {
          throw new Error('demographics のエンドポイントが未設定です');
        }
        const groups = ((suggestionData as any).groups ?? []) as Array<any>;
        if (!Array.isArray(groups) || groups.length === 0) {
          throw new Error('属性提案データが存在しません');
        }
        const payloadGroups = groups
          .map((group: any) => {
            const campaignId = String(group.campaign_id ?? '').trim();
            const adGroupId = String(group.ad_group_id ?? '').trim();
            const items = Array.isArray(group.items) ? group.items : [];
            const payloadItems = items.map((item: any) => {
              const currentValueRaw =
                formData[item.id] !== undefined ? formData[item.id] : item.suggested_bid_modifier;
              const currentValue = normalizeDemographicsToggle(currentValueRaw);
              return {
                id: String(item.id),
                suggested_bid_modifier: currentValue,
              };
            });
            return {
              campaign_id: campaignId,
              ad_group_id: adGroupId,
              items: payloadItems,
            };
          })
          .filter(
            (g: any) =>
              g.campaign_id && g.ad_group_id && Array.isArray(g.items) && g.items.length > 0
          );

        if (payloadGroups.length === 0) {
          throw new Error('demographics の適用対象がありません');
        }
        return [
          {
            endpoint,
            payload: {
              project_id: projectId,
              ...buildActionMeta(suggestionId, executorType),
              groups: payloadGroups,
            },
          },
        ];
      }
      case 'ad_schedule': {
        const endpoint = platform ? APPLY_ENDPOINTS[platform]?.ad_schedule : undefined;
        if (!endpoint) {
          throw new Error('ad_schedule のエンドポイントが未設定です');
        }
        const schedules = ((suggestionData as any).schedules ?? []) as Array<any>;
        if (!Array.isArray(schedules) || schedules.length === 0) {
          throw new Error('ad_schedule の提案データが存在しません');
        }

        const targets = schedules
          .map((cp: any) => {
            const campaignId = String(cp.campaign_id ?? '').trim();
            const selected = formData[campaignId];
            const entries = Array.isArray(selected)
              ? selected
              : Array.isArray(cp.suggested_schedules)
                ? cp.suggested_schedules
                : [];
            return {
              endpoint,
              payload: {
                project_id: projectId,
                ...buildActionMeta(suggestionId, executorType),
                campaign_id: campaignId,
                suggested_schedules: entries.map((x: any) => ({
                  id: String(x?.id ?? ''),
                  day: String(x?.day ?? ''),
                  start: String(x?.start ?? ''),
                  end: String(x?.end ?? ''),
                })),
              },
            };
          })
          .filter(
            (t: any) =>
              t.payload.campaign_id &&
              Array.isArray(t.payload.suggested_schedules) &&
              t.payload.suggested_schedules.length > 0
          );

        if (targets.length === 0) {
          throw new Error('ad_schedule の適用対象がありません');
        }
        return targets;
      }
      case 'location_targeting': {
        const endpoint = platform ? APPLY_ENDPOINTS[platform]?.location_targeting : undefined;
        if (!endpoint) {
          throw new Error('location_targeting のエンドポイントが未設定です');
        }
        const locations = ((suggestionData as any).locations ?? []) as Array<any>;
        if (!Array.isArray(locations) || locations.length === 0) {
          throw new Error('location_targeting の提案データが存在しません');
        }

        const targets = locations
          .map((cp: any) => {
            const campaignId = String(cp.campaign_id ?? '').trim();
            const selected = formData[campaignId];
            const entries = Array.isArray(selected)
              ? selected
              : Array.isArray(cp.suggested_locations)
                ? cp.suggested_locations
                : [];
            return {
              endpoint,
              payload: {
                project_id: projectId,
                ...buildActionMeta(suggestionId, executorType),
                campaign_id: campaignId,
                suggested_locations: entries.map((x: any) => ({
                  id: String(x?.id ?? ''),
                  type:
                    String(x?.type ?? 'TARGET').toUpperCase() === 'EXCLUDE' ? 'EXCLUDE' : 'TARGET',
                })),
              },
            };
          })
          .filter(
            (t: any) =>
              t.payload.campaign_id &&
              Array.isArray(t.payload.suggested_locations) &&
              t.payload.suggested_locations.length > 0
          );
        if (targets.length === 0) {
          throw new Error('location_targeting の適用対象がありません');
        }
        return targets;
      }
      default:
        throw new Error(`未対応のフォーム種別です: ${suggestionData.formType}`);
    }
  };

  // 4. 送信処理
  const handleApply = async () => {
    setSubmitStatus('loading');
    setSubmitErrorMessage('');

    try {
      if (!canApplySuggestion) {
        throw new Error('この提案種別はまだ媒体反映に未対応です。提案内容の確認まで利用できます。');
      }
      const applyTargets = buildApplyTargets();
      if (applyTargets.length === 0) {
        throw new Error('適用対象がありません');
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      if (!backendUrl) {
        throw new Error('REACT_APP_BACKEND_URL が未設定です');
      }

      const responses: any[] = [];
      for (const target of applyTargets) {
        const response = await fetch(`${backendUrl}${target.endpoint}`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(target.payload),
        });

        let responseJson: any = null;
        try {
          responseJson = await response.json();
        } catch {
          responseJson = null;
        }

        if (!response.ok) {
          const msg =
            responseJson?.detail ||
            responseJson?.error ||
            `API呼び出しに失敗しました (${response.status})`;
          throw new Error(String(msg));
        }
        responses.push(responseJson);
      }

      const hasFailedResult = responses.some((r) => r && r.ok === false);
      if (hasFailedResult) {
        const failed = responses.find((r) => r && r.ok === false);
        const demographicsNestedError = (() => {
          const groups = failed?.details?.demographics?.groups;
          if (!Array.isArray(groups)) return null;
          for (const g of groups) {
            const items = g?.items;
            if (!Array.isArray(items)) continue;
            const bad = items.find((it: any) => it && it.ok === false && it.error);
            if (bad?.error) return String(bad.error);
          }
          return null;
        })();
        const detail =
          failed?.detail ||
          failed?.error ||
          failed?.message ||
          failed?.details?.bidding_strategy?.error ||
          failed?.details?.bidding_strategy?.reason ||
          failed?.details?.status?.error ||
          failed?.details?.status?.reason ||
          failed?.details?.budget?.error ||
          failed?.details?.budget?.reason ||
          failed?.details?.add_image_asset?.error ||
          failed?.details?.add_image_asset?.reason ||
          failed?.details?.replace_image_asset?.error ||
          failed?.details?.replace_image_asset?.reason ||
          failed?.details?.location_targeting?.error ||
          failed?.details?.location_targeting?.reason ||
          failed?.details?.demographics?.error ||
          failed?.details?.demographics?.reason ||
          demographicsNestedError ||
          failed?.details?.keyword_targeting?.error ||
          failed?.details?.keyword_targeting?.reason;
        throw new Error(detail ? String(detail) : '一部のキャンペーンで適用に失敗しました');
      }

      await onApplied?.();
      setSubmitStatus('success');
    } catch (error) {
      console.error('[SuggestionModal] apply failed:', error);
      setSubmitErrorMessage(
        error instanceof Error ? error.message : '広告媒体への反映に失敗しました。'
      );
      setSubmitStatus('error');
    }
  };

  const handleClose = () => {
    setSubmitStatus('idle');
    onClose();
  };

  const renderConfirmListItems = () => {
    switch (suggestionData?.formType) {
      case 'budget':
        return (
          <>
            {(suggestionData as any).campaigns?.map((cp: any) => (
              <li key={cp.campaign_id}>
                <div className={styles.confirmItemName}>{cp.campaign_name}</div>
                <div className={styles.confirmItemChange}>
                  <span className={styles.before}>
                    {Number(cp.before_budget).toLocaleString()}円
                  </span>
                  <span className={styles.arrow}>→</span>
                  <span className={styles.after}>
                    {formData[cp.campaign_id]
                      ? Number(String(formData[cp.campaign_id]).replace(/,/g, '')).toLocaleString()
                      : '0'}
                    円
                  </span>
                </div>
              </li>
            ))}
          </>
        );
      case 'device_targeting':
        return (
          <>
            {(suggestionData as any).devices?.map((dev: any) => {
              const normalizeToggle = (v: any) => (v === -1.0 ? -1.0 : 0.0);
              const beforeVal = normalizeToggle(dev.before_bid_modifier);
              const afterRaw = formData[dev.device_type];
              if (afterRaw === undefined) return null;
              const afterVal = normalizeToggle(afterRaw);
              if (afterVal === beforeVal) return null;
              const formatVal = (v: any) => (normalizeToggle(v) === -1.0 ? '配信OFF' : '配信ON');
              return (
                <li key={dev.device_type}>
                  <div className={styles.confirmItemName}>{dev.device_label}</div>
                  <div className={styles.confirmItemChange}>
                    <span className={styles.before}>{formatVal(beforeVal)}</span>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.after}>{formatVal(afterVal)}</span>
                  </div>
                </li>
              );
            })}
          </>
        );
      case 'bidding_strategy':
        return (
          <>
            {(suggestionData as any).suggestions?.map((sg: any) => {
              const fd = formData[sg.campaign_id];
              if (!fd) return null;
              const stratLabel = (type: string) => {
                if (type === 'TARGET_CPA') return '目標コンバージョン単価';
                if (type === 'TARGET_ROAS') return '目標広告費用対効果';
                if (type === 'MAXIMIZE_CONVERSIONS') return 'コンバージョン数最大化';
                if (type === 'MAXIMIZE_CONVERSION_VALUE') return 'コンバージョン値最大化';
                if (type === 'TARGET_IMPRESSION_SHARE') return '目標インプレッションシェア';
                if (type === 'MAXIMIZE_CLICKS' || type === 'TARGET_SPEND')
                  return 'クリック数最大化';
                return type;
              };
              return (
                <li key={sg.campaign_id}>
                  <div className={styles.confirmItemName}>{sg.campaign_name}</div>
                  <div className={styles.confirmItemChange}>
                    <span className={styles.before}>
                      {stratLabel(sg.before_strategy.type)}{' '}
                      {sg.before_strategy.value ? `(${sg.before_strategy.value})` : ''}
                    </span>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.after}>
                      {stratLabel(fd.type)} {fd.value ? `(${fd.value})` : ''}
                    </span>
                  </div>
                </li>
              );
            })}
          </>
        );
      case 'keyword_targeting':
        return (
          <>
            {(suggestionData as any).keywords?.map((kw: any) => {
              const checked = formData[kw.keyword_id];
              return (
                <li key={kw.keyword_id}>
                  <div className={styles.confirmItemName}>{kw.keyword_text}</div>
                  <div className={styles.confirmItemChange}>
                    <span className={styles.before}>変更なし</span>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.after}>{checked ? '変更なし' : '停止・削除'}</span>
                  </div>
                </li>
              );
            })}
          </>
        );
      case 'cross_platform':
        return (
          <>
            {(suggestionData as any).crossPlatformBudgets?.map((cp: any) => (
              <li key={cp.campaign_id}>
                <div className={styles.confirmItemName}>{cp.campaign_name}</div>
                <div className={styles.confirmItemChange}>
                  <span className={styles.before}>
                    {Number(cp.before_budget).toLocaleString()}円
                  </span>
                  <span className={styles.arrow}>→</span>
                  <span className={styles.after}>
                    {formData[cp.campaign_id]
                      ? Number(String(formData[cp.campaign_id]).replace(/,/g, '')).toLocaleString()
                      : '0'}
                    円
                  </span>
                </div>
              </li>
            ))}
          </>
        );
      case 'campaign_status':
        return (
          <>
            {(suggestionData as any).campaigns?.map((cp: any) => {
              const afterStatus =
                formData[cp.campaign_id] ?? cp.suggested_status ?? cp.before_status;
              const toLabel = (status: 'ACTIVE' | 'PAUSED') =>
                status === 'ACTIVE' ? '配信中' : '一時停止';
              return (
                <li key={cp.campaign_id}>
                  <div className={styles.confirmItemName}>{cp.campaign_name}</div>
                  <div className={styles.confirmItemChange}>
                    <span className={styles.before}>{toLabel(cp.before_status)}</span>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.after}>{toLabel(afterStatus)}</span>
                  </div>
                </li>
              );
            })}
          </>
        );
      case 'ad_text':
        return (
          <>
            {(suggestionData as any).ads?.map((ad: any) => {
              const fd = formData[ad.ad_id];
              if (!fd) return null;

              const changedHeadlines = (ad.headlines || []).filter((h: any) => h.is_changed);
              const changedDescriptions = (ad.descriptions || []).filter((d: any) => d.is_changed);

              return (
                <li key={ad.ad_id}>
                  <div className={styles.confirmItemName}>{ad.ad_group_name}</div>
                  <div className={`${styles.confirmItemChange} ${styles.confirmItemChangeColumn}`}>
                    {changedHeadlines.map((h: any) => (
                      <div key={h.id} className={styles.confirmRowRSA}>
                        <div className={styles.confirmRowContent}>
                          <span className={styles.beforeLabel}>[見出し]</span>
                          <span className={styles.before}>{h.before_text || '(新規追加)'}</span>
                          <span className={styles.arrow}>→</span>
                          <span className={styles.after}>{fd.headlines[h.id]}</span>
                        </div>
                      </div>
                    ))}
                    {changedDescriptions.map((d: any) => (
                      <div key={d.id} className={styles.confirmRowRSA}>
                        <div className={styles.confirmRowContent}>
                          <span className={styles.beforeLabel}>[説明文]</span>
                          <span className={styles.before}>{d.before_text || '(新規追加)'}</span>
                          <span className={styles.arrow}>→</span>
                          <span className={styles.after}>{fd.descriptions[d.id]}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </li>
              );
            })}
          </>
        );
      case 'add_image_asset':
        return (
          <>
            {(suggestionData as any).suggestions?.map((sg: any) => {
              const file = formData[sg.ad_group_id] as AddImageAssetUploadValue | null | undefined;
              return (
                <li key={sg.ad_group_id}>
                  <div className={styles.confirmItemName}>{sg.ad_group_name}</div>
                  <div className={styles.confirmItemChange}>
                    <span className={styles.before}>素材不足</span>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.after}>
                      {file?.fileName ? `[追加] ${file.fileName}` : '追加なし'}
                    </span>
                  </div>
                </li>
              );
            })}
          </>
        );
      case 'replace_image_asset':
        return (
          <>
            {(suggestionData as any).assets?.map((as: any) => {
              const file = formData[as.asset_id] as ReplaceImageAssetUploadValue | null | undefined;
              return (
                <li key={as.asset_id}>
                  <div className={styles.confirmItemName}>{as.ad_group_name}</div>
                  <div className={styles.confirmItemChange}>
                    <span className={styles.before}>既存画像</span>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.after}>
                      {file?.fileName ? `[差し替え] ${file.fileName}` : '変更なし'}
                    </span>
                  </div>
                </li>
              );
            })}
          </>
        );
      case 'ad_schedule':
        return (
          <>
            {(suggestionData as any).schedules?.map((cp: any) => {
              const currentValue = formData[cp.campaign_id];
              const beforeValue = cp.before_schedules ?? cp.before_schedule;
              const afterValue = currentValue ?? cp.suggested_schedules ?? cp.suggested_schedule;
              const formatSchedule = (value: any) => {
                if (Array.isArray(value)) {
                  if (value.length === 0) return 'スケジュール設定なし';
                  return value.map((sch: any) => `${sch.day} ${sch.start} - ${sch.end}`).join(', ');
                }
                if (typeof value === 'string' && value.trim()) return value;
                return 'スケジュール設定なし';
              };
              return (
                <li key={cp.campaign_id}>
                  <div className={styles.confirmItemName}>{cp.campaign_name}</div>
                  <div className={styles.confirmItemChange}>
                    <span className={styles.before}>{formatSchedule(beforeValue)}</span>
                    <span className={styles.arrow}>→</span>
                    <span className={styles.after}>{formatSchedule(afterValue)}</span>
                  </div>
                </li>
              );
            })}
          </>
        );
      case 'location_targeting':
        return (
          <>
            {(suggestionData as any).locations?.map((cp: any) => {
              const currentListRaw = formData[cp.campaign_id] || cp.suggested_locations;

              // Helper to show changes succinctly
              const formatLocList = (list: any) => {
                const normalized = Array.isArray(list) ? list : [];
                if (normalized.length === 0) return '設定なし';
                return normalized
                  .map((l: any) => {
                    const locLabel = l?.label || l?.name || l?.id || '不明な地域';
                    return `${locLabel}(${l.type === 'TARGET' ? '配信' : '除外'})`;
                  })
                  .join(', ');
              };

              const beforeStr = formatLocList(cp.current_locations);
              const afterStr = formatLocList(currentListRaw);

              return (
                <li key={cp.campaign_id}>
                  <div className={styles.confirmItemName}>{cp.campaign_name}</div>
                  <div className={`${styles.confirmItemChange} ${styles.confirmItemChangeColumn}`}>
                    <div className={`${styles.before} ${styles.confirmBeforeLabelText}`}>
                      [変更前] {beforeStr}
                    </div>
                    <div className={styles.after}>[変更後] {afterStr}</div>
                  </div>
                </li>
              );
            })}
          </>
        );
      case 'demographics':
        return (
          <>
            {(suggestionData as any).groups?.map((group: any) => (
              <React.Fragment key={group.ad_group_id || group.campaign_id}>
                {group.items.map((item: any) => {
                  const currentValueRaw =
                    formData[item.id] !== undefined
                      ? formData[item.id]
                      : item.suggested_bid_modifier;
                  const currentValue = normalizeDemographicsToggle(currentValueRaw);
                  const beforeValue = normalizeDemographicsToggle(item.before_bid_modifier);
                  const isChanged = currentValue !== beforeValue;
                  if (!isChanged) return null; // Only show items that are actually changed in the confirm view
                  return (
                    <li key={item.id}>
                      <div className={styles.confirmItemName}>
                        {group.ad_group_name
                          ? `${group.campaign_name} / ${group.ad_group_name}`
                          : group.campaign_name}{' '}
                        - {item.label}
                      </div>
                      <div className={styles.confirmItemChange}>
                        <span className={styles.before}>
                          {beforeValue === null ? '配信 ON' : '配信 OFF'}
                        </span>
                        <span className={styles.arrow}>→</span>
                        <span className={styles.after}>
                          {currentValue === null ? '配信 ON' : '配信 OFF'}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </React.Fragment>
            ))}
          </>
        );
      default:
        return null;
    }
  };

  const renderConfirmView = () => (
    <div className={styles.confirmWrapper}>
      <h3 className={styles.confirmWrapperTitle}>作業内容の確認</h3>
      <div className={styles.confirmList}>
        <ul>{renderConfirmListItems()}</ul>
      </div>
    </div>
  );

  if (!open || !suggestionData) return null;

  return (
    <div className={styles.suggestionModalOverlay} onClick={onClose}>
      <div className={styles.suggestionModalWrap} onClick={(e) => e.stopPropagation()}>
        <div className={styles.suggestionModal}>
          <div className={styles.modalHeader}>
            <div className={styles.headerLeft}>
              <div className={styles.titleArea}>
                <div className={styles.titleRow}>
                  <h3>{suggestionData?.title}</h3>
                  <div className={styles.lastUpdateInfo}>
                    {suggestionData?.suggestionDate && (
                      <span className={styles.suggestionDate}>
                        提案日時: {suggestionData.suggestionDate}
                      </span>
                    )}
                  </div>
                </div>
                {suggestionData?.breadcrumbs && suggestionData.breadcrumbs.length > 0 && (
                  <div className={styles.headerBreadcrumbs}>
                    {suggestionData.breadcrumbs.map((crumb, idx) => (
                      <React.Fragment key={idx}>
                        <div className={`${styles.crumb} ${styles[`crumb_${crumb.level}`]}`}>
                          {crumb.label}
                        </div>
                        {idx < suggestionData.breadcrumbs.length - 1 && (
                          <span className={styles.crumbSeparator}>&gt;</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.headerActions}>
              {submitStatus === 'idle' && (
                <>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={handleDeleteSuggestion}
                  >
                    この提案を削除
                  </button>
                  <button type="button" className={styles.cancelButton} onClick={handleClose}>
                    キャンセル
                  </button>
                  {canApplySuggestion && (
                    <button
                      type="button"
                      className={styles.positiveButton}
                      onClick={() => setSubmitStatus('confirm')}
                    >
                      作業依頼
                    </button>
                  )}
                </>
              )}
              {submitStatus === 'confirm' && (
                <>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => setSubmitStatus('idle')}
                  >
                    戻る
                  </button>
                  <button type="button" className={styles.positiveButton} onClick={handleApply}>
                    実行
                  </button>
                </>
              )}
              {(submitStatus === 'success' || submitStatus === 'error') && (
                <button type="button" className={styles.cancelButton} onClick={handleClose}>
                  閉じる
                </button>
              )}
            </div>
          </div>

          <div className={styles.mainContent}>
            {submitStatus === 'idle' && (
              <>
                {suggestionData?.lastUpdatedDate && (
                  <div className={styles.topUpdateInfo}>
                    <span className={styles.updateDate}>
                      前回更新日: {suggestionData.lastUpdatedDate}
                    </span>
                    {isLessThanTwoWeeks(suggestionData.lastUpdatedDate) && (
                      <span className={styles.updateWarning}>
                        ※前回変更から2週間経過していません
                      </span>
                    )}
                  </div>
                )}
                <div className={styles.aiAdvice}>
                  <AibexIcon className={styles.aibexIcon} />
                  <p>{suggestionData?.aiComment}</p>
                </div>

                {/* プラットフォームとフォームタイプによる出し分け */}
                {(['GOOGLE', 'META', 'YAHOO_SEARCH', 'YAHOO_DISPLAY'] as SuggestionPlatform[]).includes(
                  suggestionData?.platform as SuggestionPlatform
                ) && (
                  <>
                    {suggestionData?.formType === 'budget' && (
                      <GoogleBudgetForm
                        campaigns={(suggestionData as any).campaigns}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                        unit={(suggestionData as any).unit}
                      />
                    )}

                    {suggestionData?.formType === 'device_targeting' && (
                      <TargetingDeviceForm
                        devices={(suggestionData as any).devices}
                        formData={formData}
                        onChange={(type, val) => setFormData((prev) => ({ ...prev, [type]: val }))}
                      />
                    )}

                    {suggestionData?.formType === 'bidding_strategy' && (
                      <BiddingStrategyForm
                        suggestions={(suggestionData as any).suggestions}
                        formData={formData}
                        onChange={(id, data) => setFormData((prev) => ({ ...prev, [id]: data }))}
                        unit={(suggestionData as any).unit}
                      />
                    )}

                    {suggestionData?.formType === 'keyword_targeting' && (
                      <TargetingKeywordForm
                        keywords={(suggestionData as any).keywords}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                      />
                    )}

                    {suggestionData?.formType === 'campaign_status' && (
                      <CampaignStatusForm
                        campaigns={(suggestionData as any).campaigns}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                      />
                    )}

                    {suggestionData?.formType === 'ad_text' && (
                      <AdTextForm
                        ads={(suggestionData as any).ads}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                        platform={suggestionData?.platform}
                      />
                    )}

                    {suggestionData?.formType === 'add_image_asset' && (
                      <AddImageAssetForm
                        suggestions={(suggestionData as any).suggestions}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                      />
                    )}

                    {suggestionData?.formType === 'replace_image_asset' && (
                      <ReplaceImageAssetForm
                        assets={(suggestionData as any).assets}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                        platform={suggestionData?.platform}
                      />
                    )}

                    {suggestionData?.formType === 'ad_schedule' && (
                      <AdScheduleForm
                        schedules={(suggestionData as any).schedules}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                      />
                    )}

                    {suggestionData?.formType === 'location_targeting' && (
                      <LocationTargetingForm
                        locations={(suggestionData as any).locations}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                      />
                    )}

                    {suggestionData?.formType === 'demographics' && (
                      <TargetingDemographicForm
                        groups={(suggestionData as any).groups}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                      />
                    )}
                  </>
                )}

                {suggestionData?.platform === 'MULTI' && (
                  <>
                    {suggestionData?.formType === 'cross_platform' && (
                      <CrossPlatformBudgetForm
                        budgets={(suggestionData as any).crossPlatformBudgets}
                        formData={formData}
                        onChange={(id, val) => setFormData((prev) => ({ ...prev, [id]: val }))}
                        unit={(suggestionData as any).unit}
                      />
                    )}
                  </>
                )}
              </>
            )}

            {submitStatus === 'confirm' && renderConfirmView()}

            {submitStatus === 'loading' && (
              <div className={styles.statusView}>
                <div className={styles.loader}></div>
                <h3>作業を実行中...</h3>
                <p>各媒体への反映を行っています。しばらくお待ちください。</p>
              </div>
            )}

            {submitStatus === 'success' && (
              <div className={styles.statusView}>
                <div className={styles.successIcon}>✓</div>
                <h3>作業が完了しました</h3>
                <p>提案内容が正常に広告媒体に反映されました。</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className={styles.statusView}>
                <div className={styles.errorIcon}>!</div>
                <h3>エラーが発生しました</h3>
                <p>
                  {submitErrorMessage ||
                    '広告媒体への反映中に問題が発生しました。時間をおいて再度お試しください。'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuggestionModal;
